import React, { useEffect, useState } from 'react';
import {
  verifyResetToken,
  completePasswordReset,
  completeEmailVerification,
  completeChangeEmail
} from '../services/emailActionsService';
import { getPasswordStrength, MIN_REQUIRED_PASSWORD_SCORE } from '../utils/passwordStrength';
import './comic.css';

// Fully custom replacement for Firebase's own hosted *.firebaseapp.com
// action-handling page, rendered by App.js whenever the URL carries
// ?action=...&token=... -- functions/index.js's sendPasswordResetLink /
// sendVerificationEmail / sendChangeEmailVerification all email a link
// here now, carrying a token of this app's OWN minting (not a Firebase
// oobCode -- see functions/index.js's long comment on why: Firebase's own
// generateXLink()/handleCodeInApp mechanism always routes through its
// *.firebaseapp.com page regardless, and the one Console setting that
// could change that is confirmed blocked for this project). This is what
// finally removes every mention of "firebase" from the password-reset/
// verify-email/change-email flow, and gives a real "back to login" button
// at the end instead of requiring a manual switch back to the app.
//
// Deliberately rendered standalone, outside every other gate/provider in
// App.js -- whoever lands here may not even be signed in on this device at
// all (e.g. a password-reset link opened in a fresh mobile browser), so
// this can't depend on SessionProvider/ProfileProvider/the user being
// signed in. It only calls the public (no-auth-required) Cloud Functions
// above via emailActionsService.js -- possession of the token in the URL
// is the only credential needed, same model as Firebase's own oobCode
// links.

// This page is always reached by tapping an email link, which iOS opens in
// Safari (or whatever the default browser is) regardless of whether the
// Pumpous app is installed on the same device -- there's no way for the
// *email client* to route the tap into the native app directly. So "back
// to the app" has to happen from here instead, once the user is done:
// attempt the native app's own custom URL scheme (`pumpous://`, registered
// in ios/App/App/Info.plist's CFBundleURLTypes) first, and only fall back
// to reloading this same web page at its bare origin if that didn't
// actually switch away from the browser -- the standard "deep link with a
// web fallback" pattern, since a plain `window.location.href` to an
// unregistered/uninstalled scheme just fails silently with the tab still
// visible. `visibilitychange` firing is the signal that iOS genuinely
// foregrounded the native app instead of staying in Safari; if it hasn't
// fired by the timeout, assume the app isn't installed (or this isn't iOS
// at all -- e.g. the link was opened on a desktop browser) and land on the
// ordinary web dashboard/Login screen instead, exactly as before this
// existed.
const goToApp = () => {
  const fallbackUrl = window.location.origin;
  let switchedAway = false;
  const onVisibilityChange = () => {
    if (document.hidden) switchedAway = true;
  };
  document.addEventListener("visibilitychange", onVisibilityChange);
  window.location.href = "pumpous://";
  setTimeout(() => {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    if (!switchedAway) {
      window.location.assign(fallbackUrl);
    }
  }, 1500);
};

const PasswordStrengthMeter = ({ password }) => {
  const { score, label, color } = getPasswordStrength(password);
  return (
    <div style={{ marginBottom: "14px" }}>
      <div style={{ display: "flex", gap: "4px", marginBottom: "4px" }}>
        {[1, 2, 3, 4].map((segment) => (
          <div
            key={segment}
            style={{
              flex: 1,
              height: "6px",
              borderRadius: "3px",
              background: segment <= score ? color : "#e0e0e0"
            }}
          />
        ))}
      </div>
      <span style={{ fontFamily: "'Anonymous Pro', monospace", fontSize: "12px", color }}>
        {label}
      </span>
    </div>
  );
};

// action="resetPassword": verify the token (non-destructively, via
// verifyActionToken), collect a new password, then consume the token via
// completePasswordReset.
const ResetPasswordAction = ({ token }) => {
  const [status, setStatus] = useState("verifying"); // verifying | ready | done | error
  const [email, setEmail] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      const result = await verifyResetToken(token);
      if (result.success) {
        setEmail(result.email);
        setStatus("ready");
      } else {
        setError(result.error);
        setStatus("error");
      }
    })();
    // token is fixed for the lifetime of this page load -- no need to
    // re-verify on any later render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const score = getPasswordStrength(password).score;
  const passwordsMatch = password && password === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (!passwordsMatch) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    const result = await completePasswordReset(token, password);
    if (result.success) {
      setStatus("done");
    } else {
      setError(result.error);
    }
    setSubmitting(false);
  };

  if (status === "verifying") {
    return <p className="comic-subtitle">Checking your link...</p>;
  }

  if (status === "error") {
    return (
      <>
        <p className="comic-subtitle">{error}</p>
        <button type="button" onClick={goToApp} className="comic-btn comic-btn--go">
          Back to Log In
        </button>
      </>
    );
  }

  if (status === "done") {
    return (
      <>
        <p className="comic-subtitle">Your password has been changed. You can log in with it now.</p>
        <button type="button" onClick={goToApp} className="comic-btn comic-btn--go">
          Back to Log In
        </button>
      </>
    );
  }

  return (
    <>
      <p className="comic-subtitle">Choose a new password for <strong>{email}</strong>.</p>
      <form onSubmit={handleSubmit}>
        <input
          type="password"
          placeholder="New Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="comic-input"
          style={{ width: "100%", marginBottom: password ? "6px" : "14px" }}
          autoFocus
        />
        {password && <PasswordStrengthMeter password={password} />}
        <input
          type="password"
          placeholder="Confirm New Password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="comic-input"
          style={{ width: "100%", marginBottom: "14px" }}
        />
        <button
          type="submit"
          disabled={submitting || score < MIN_REQUIRED_PASSWORD_SCORE || !passwordsMatch}
          className="comic-btn comic-btn--go"
        >
          {submitting ? "Saving..." : "Save New Password"}
        </button>
        {error && <div className="comic-alert-bubble">{error}</div>}
      </form>
    </>
  );
};

// action="verifyEmail" / action="changeEmail": both are a single one-shot
// call on mount (completeFn), differing only in which function and which
// success copy to show -- there's no form to fill in first, unlike
// resetPassword above.
const CompleteAction = ({ token, completeFn, successText }) => {
  const [status, setStatus] = useState("working"); // working | done | error
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      const result = await completeFn(token);
      if (result.success) {
        setStatus("done");
      } else {
        setError(result.error);
        setStatus("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status === "working") {
    return <p className="comic-subtitle">Confirming...</p>;
  }

  if (status === "error") {
    return (
      <>
        <p className="comic-subtitle">{error}</p>
        <button type="button" onClick={goToApp} className="comic-btn comic-btn--go">
          Back to Pumpous
        </button>
      </>
    );
  }

  return (
    <>
      <p className="comic-subtitle">{successText}</p>
      <button type="button" onClick={goToApp} className="comic-btn comic-btn--go">
        Continue to Pumpous
      </button>
    </>
  );
};

const AuthAction = ({ action, token }) => {
  let body;
  let heading;

  if (action === "resetPassword") {
    heading = "Reset Your Password";
    body = <ResetPasswordAction token={token} />;
  } else if (action === "verifyEmail") {
    heading = "Verify Your Email";
    body = (
      <CompleteAction
        token={token}
        completeFn={completeEmailVerification}
        successText="Your email is verified. Welcome aboard, Hero."
      />
    );
  } else if (action === "changeEmail") {
    heading = "Confirm Your New Email";
    body = (
      <CompleteAction
        token={token}
        completeFn={completeChangeEmail}
        successText="Your new email address is confirmed."
      />
    );
  } else {
    heading = "Unknown Link";
    body = (
      <>
        <p className="comic-subtitle">That link isn't one we recognize.</p>
        <button type="button" onClick={goToApp} className="comic-btn comic-btn--go">
          Back to Pumpous
        </button>
      </>
    );
  }

  // minHeight:100vh + its own explicit white background -- body's own
  // background is dark Primary now (index.css), used specifically so
  // WebKit fills the iOS notch/Dynamic Island "unsafe area" with that
  // dark color instead of white when this page is opened in the native
  // app's own WebView (e.g. via the pumpous:// deep link after a
  // password reset). Spans the full viewport so none of that dark body
  // shows through on the sides/below this page's own narrower, centered
  // content, in a plain browser tab too.
  return (
    // overflow: "hidden" is load-bearing here too, same reasoning as
    // App.js's analogous wrapper -- without it, the inner column's own
    // `margin: "8px auto"` collapses through this wrapper and through
    // body itself, leaving a thin sliver of body's own (now dark)
    // background visible above it.
    <div style={{ minHeight: "100vh", background: "#ffffff", overflow: "hidden" }}>
      <div style={{ paddingTop: "20px", paddingLeft: "20px", paddingRight: "20px", maxWidth: "500px", margin: "8px auto" }}>
        <div className="comic-panel comic-panel--outer-active" style={{ textAlign: "center" }}>
          <h2 className="comic-title" style={{ fontSize: "26px" }}>{heading}</h2>
          {body}
        </div>
      </div>
    </div>
  );
};

export default AuthAction;
